import _defineProperty from "/usr/src/app/node_modules/@babel/runtime/helpers/esm/defineProperty";
import _slicedToArray from "/usr/src/app/node_modules/@babel/runtime/helpers/esm/slicedToArray";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) { symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); } keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

import React, { createContext, useCallback, useMemo, useState, useEffect, useRef, cloneElement, Children, memo } from 'react';
import { Overlay } from 'react-overlays';
import { uniqueId } from '../../lib/uniqueId.js';
import FadeTransition from '../Transitions/Fade.js';
import { applyStylingHoc } from '../../lib/styling.js';

var thisVoid = function thisVoid() {};

export var OverlayContext = createContext({
  closeOverlay: thisVoid,
  overlayId: ''
});

var contains = function contains(arrayOrString, value) {
  return arrayOrString instanceof Array ? arrayOrString.indexOf(value) > -1 : arrayOrString === value;
};

var defaultTrigger = ['click'];
var defaultPlacement = 'top';
export var OverlayTrigger = React.forwardRef(function Trigger(props, ref) {
  var animation = props.animation,
      children = props.children,
      overlay = props.overlay,
      _props$delay = props.delay,
      delay = _props$delay === void 0 ? 0 : _props$delay,
      _props$delayHide = props.delayHide,
      delayHide = _props$delayHide === void 0 ? null : _props$delayHide,
      _props$delayShow = props.delayShow,
      delayShow = _props$delayShow === void 0 ? null : _props$delayShow,
      _props$placement = props.placement,
      placement = _props$placement === void 0 ? defaultPlacement : _props$placement,
      _props$trigger = props.trigger,
      trigger = _props$trigger === void 0 ? defaultTrigger : _props$trigger,
      _props$defaultOverlay = props.defaultOverlayShown,
      defaultOverlayShown = _props$defaultOverlay === void 0 ? false : _props$defaultOverlay,
      onShow = props.onShow,
      onHide = props.onHide,
      _props$rootClose = props.rootClose,
      rootClose = _props$rootClose === void 0 ? false : _props$rootClose,
      _props$allowDefaultEv = props.allowDefaultEvent,
      allowDefaultEvent = _props$allowDefaultEv === void 0 ? false : _props$allowDefaultEv;

  var _useState = useState({
    shown: defaultOverlayShown
  }),
      _useState2 = _slicedToArray(_useState, 2),
      state = _useState2[0],
      setState = _useState2[1];

  var shown = state.shown; // REF: TS: get rid of any

  var target = useRef(null);
  var timerIdRef = useRef(null); // @ts-ignore

  timerIdRef.current = 0;
  var timerId = timerIdRef.current;

  var _id = useMemo(function () {
    return 'overlay-' + uniqueId();
  }, []);

  useEffect(function () {
    if (shown && onShow) {
      onShow();
    }

    if (!shown && onHide) {
      onHide();
    }
  }, [onHide, onShow, shown]);
  var updateVisibility = useCallback(function () {
    var newVisible = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : null;

    if (newVisible !== shown) {
      var visible = newVisible === null ? !shown : newVisible;

      var updateState = function updateState() {
        return setState({
          shown: visible
        });
      };

      var delays = visible ? delayShow !== null && delayShow !== undefined ? delayShow : delay : delayHide !== null && delayHide !== undefined ? delayHide : delay;

      if (delays > 0) {
        // @ts-ignore
        timerIdRef.current = setTimeout(updateState, delays);
      } else {
        updateState();
      }
    } else if (timerId) {
      clearTimeout(timerId); // @ts-ignore

      timerIdRef.current = 0;
    }
  }, [shown, delay, delayHide, delayShow, timerId]);
  var hide = useCallback(function () {
    return updateVisibility(false);
  }, [updateVisibility]);
  var toggle = useCallback(function () {
    return updateVisibility(null);
  }, [updateVisibility]);
  var show = useCallback(function () {
    return updateVisibility(true);
  }, [updateVisibility]);
  useEffect(function () {
    if (ref) {
      if (typeof ref === 'function') {
        ref({
          show: show,
          hide: hide,
          toggle: toggle
        });
      } else {
        // @ts-ignore
        ref.current = {
          show: show,
          hide: hide,
          toggle: toggle
        }; // REF: Attempt to assign to const or readonly variable
      }
    }
  }, [hide, ref, show, toggle]);
  var handleClick = useCallback(function (e) {
    if (!allowDefaultEvent) {
      e.preventDefault();
      e.stopPropagation();
    }

    toggle();
  }, [toggle, allowDefaultEvent]);
  var createTriggerProps = useCallback(function () {
    var triggerProps = {
      'aria-describedby': shown ? _id : null,
      ref: target,
      key: 'trigger'
    };

    if (contains(trigger, 'click')) {
      triggerProps.onClick = handleClick;
    }

    if (contains(trigger, 'focus')) {
      triggerProps.onFocus = show;
      triggerProps.onBlur = hide;
    }

    if (contains(trigger, 'hover')) {
      triggerProps.onMouseOver = show;
      triggerProps.onMouseOut = hide;
    }

    return triggerProps;
  }, [_id, handleClick, hide, show, shown, trigger]);
  var contextValue = useMemo(function () {
    return {
      closeOverlay: hide,
      overlayId: _id
    };
  }, [hide, _id]);
  return React.createElement(OverlayContext.Provider, {
    value: contextValue
  }, cloneElement(Children.only(children), createTriggerProps()), React.createElement(Overlay, _objectSpread(_objectSpread({}, props), {}, {
    rootClose: rootClose,
    show: shown,
    onHide: hide,
    target: target.current,
    // @ts-ignore
    transition: animation === true ? FadeTransition : animation,
    placement: placement.split(/-/)[0],
    key: "overlay"
  }), overlay));
});
export default applyStylingHoc({})(memo(OverlayTrigger));